const { getPointer, NULL } = Box2D;

export const CUT_MARGIN = {humanoid: 0.2, fruit: 0.05};
const SPLIT_FRACTION = 0.97;

// Convex outlines of a body's fixtures in body space, grown by margin so they
// cover the drawn art rather than the slightly smaller physics shapes.
export const bodyShapes = (body, margin) => {
	const shapes = [];
	for (let fixture = body.GetFixtureList(); getPointer(fixture) !== getPointer(NULL); fixture = fixture.GetNext()) {
		if (fixture.IsSensor()) continue;
		const shape = fixture.GetShape();
		if (shape.GetType() === Box2D.b2Shape.e_circle) {
			const circle = Box2D.castObject(shape, Box2D.b2CircleShape);
			const center = circle.get_m_p();
			shapes.push({x: center.get_x(), y: center.get_y(), r: circle.get_m_radius() + margin});
		} else if (shape.GetType() === Box2D.b2Shape.e_polygon) {
			const polygon = Box2D.castObject(shape, Box2D.b2PolygonShape);
			const count = polygon.get_m_count();
			const vertices = [];
			for (let i = 0; i < count; i++) {
				const vertex = polygon.get_m_vertices(i);
				vertices.push({x: vertex.get_x(), y: vertex.get_y()});
			}
			const normals = [];
			const offsets = [];
			for (let i = 0; i < count; i++) {
				const a = vertices[i];
				const b = vertices[(i + 1) % count];
				const length = Math.sqrt((b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y)) || 1;
				const normal = {x: (b.y - a.y) / length, y: -(b.x - a.x) / length};
				normals.push(normal);
				offsets.push(normal.x * a.x + normal.y * a.y + margin);
			}
			const points = vertices.map((vertex, i) => {
				const previous = (i + count - 1) % count;
				const n1 = normals[previous];
				const n2 = normals[i];
				const det = n1.x * n2.y - n1.y * n2.x;
				if (Math.abs(det) < 1e-9) return {x: vertex.x + n2.x * margin, y: vertex.y + n2.y * margin};
				return {
					x: (offsets[previous] * n2.y - n1.y * offsets[i]) / det,
					y: (n1.x * offsets[i] - offsets[previous] * n2.x) / det,
				};
			});
			shapes.push({normals, offsets, points});
		}
	}
	return shapes;
};

// Where the line q + d*t runs inside a shape, as [t0, t1].
const shapeInterval = (shape, qx, qy, dx, dy) => {
	if (shape.r !== undefined) {
		const fx = qx - shape.x;
		const fy = qy - shape.y;
		const b = fx * dx + fy * dy;
		const disc = b * b - (fx * fx + fy * fy - shape.r * shape.r);
		if (disc <= 0) return null;
		const root = Math.sqrt(disc);
		return [-b - root, -b + root];
	}
	let t0 = -Infinity;
	let t1 = Infinity;
	for (let i = 0; i < shape.normals.length; i++) {
		const normal = shape.normals[i];
		const room = shape.offsets[i] - (normal.x * qx + normal.y * qy);
		const rate = normal.x * dx + normal.y * dy;
		if (rate === 0) {
			if (room < 0) return null;
			continue;
		}
		const t = room / rate;
		if (rate < 0) t0 = Math.max(t0, t);
		else t1 = Math.min(t1, t);
		if (t0 >= t1) return null;
	}
	return [t0, t1];
};

const mergeIntervals = intervals => {
	intervals.sort((a, b) => a[0] - b[0]);
	const merged = [];
	for (const interval of intervals) {
		const last = merged[merged.length - 1];
		if (last && interval[0] <= last[1]) last[1] = Math.max(last[1], interval[1]);
		else merged.push([interval[0], interval[1]]);
	}
	return merged;
};

export const beamToLocal = (body, beam) => {
	const position = body.GetPosition();
	const angle = body.GetAngle();
	const c = Math.cos(angle);
	const s = Math.sin(angle);
	const ox = beam.x - position.x;
	const oy = beam.y - position.y;
	return {
		qx: c * ox + s * oy,
		qy: -s * ox + c * oy,
		dx: c * beam.dx + s * beam.dy,
		dy: -s * beam.dx + c * beam.dy,
	};
};

// Beam parameters (metres from the lens) where the beam runs through the shapes.
export const beamIntervals = (shapes, local, length) => {
	const intervals = [];
	for (const shape of shapes) {
		const interval = shapeInterval(shape, local.qx, local.qy, local.dx, local.dy);
		if (!interval) continue;
		const t0 = Math.max(0, interval[0]);
		const t1 = Math.min(length, interval[1]);
		if (t1 > t0) intervals.push([t0, t1]);
	}
	return mergeIntervals(intervals);
};

// Cut progress is kept per body, in body space, so it survives the body or the
// beam moving and adds up over several passes or several lasers.
export const cutState = (body, kind) => {
	if (body.depthCutState) return body.depthCutState;
	const shapes = bodyShapes(body, CUT_MARGIN[kind]);
	const points = [];
	for (const shape of shapes) {
		if (shape.r !== undefined) {
			points.push(shape.x, shape.y);
			for (const [ring, count] of [[0.5, 8], [1, 16]]) {
				for (let i = 0; i < count; i++) {
					const angle = i * Math.PI * 2 / count;
					points.push(shape.x + Math.cos(angle) * shape.r * ring, shape.y + Math.sin(angle) * shape.r * ring);
				}
			}
		} else {
			const count = shape.points.length;
			let cx = 0;
			let cy = 0;
			shape.points.forEach(point => {
				cx += point.x / count;
				cy += point.y / count;
			});
			points.push(cx, cy);
			shape.points.forEach((point, i) => {
				const next = shape.points[(i + 1) % count];
				points.push(point.x, point.y, (point.x + cx) / 2, (point.y + cy) / 2, (point.x + next.x) / 2, (point.y + next.y) / 2);
			});
		}
	}
	body.depthCutState = {
		kind,
		shapes,
		samples: Float64Array.from(points),
		swept: new Uint8Array(points.length / 2),
		count: 0,
		frontier: null,
	};
	return body.depthCutState;
};

export const cutFraction = state => state.count / state.swept.length;
export const isFullyCut = state => cutFraction(state) >= SPLIT_FRACTION;

export const newTrack = state => ({
	state,
	side: new Float64Array(state.swept.length),
	along: new Float64Array(state.swept.length),
	length: 0,
	primed: false,
	inBeam: false,
	step: 0,
});

// Marks the samples that crossed the beam line within its length since the
// previous step and returns how many there were.
export const advanceCut = (track, local, length) => {
	const state = track.state;
	const samples = state.samples;
	const normalX = -local.dy;
	const normalY = local.dx;
	const reach = Math.max(track.length, length);
	let swept = 0;
	for (let i = 0; i < state.swept.length; i++) {
		const x = samples[i * 2] - local.qx;
		const y = samples[i * 2 + 1] - local.qy;
		const side = x * normalX + y * normalY;
		const along = x * local.dx + y * local.dy;
		if (track.primed && !state.swept[i]) {
			const previous = track.side[i];
			if ((previous < 0 && side >= 0) || (previous > 0 && side <= 0)) {
				const crossing = track.along[i] + (along - track.along[i]) * previous / (previous - side);
				if (crossing >= 0 && crossing <= reach) {
					state.swept[i] = 1;
					state.count++;
					swept++;
				}
			}
		}
		track.side[i] = side;
		track.along[i] = along;
	}
	track.length = length;
	track.primed = true;
	return swept;
};

// The cut's edge as a circle in body space: centred on the part, as big as it.
export const cutRing = shapes => {
	let area = 0;
	let x = 0;
	let y = 0;
	for (const shape of shapes) {
		if (shape.r !== undefined) {
			const circle = Math.PI * shape.r * shape.r;
			area += circle;
			x += shape.x * circle;
			y += shape.y * circle;
			continue;
		}
		const points = shape.points;
		for (let i = 0; i < points.length; i++) {
			const a = points[i];
			const b = points[(i + 1) % points.length];
			const cross = a.x * b.y - b.x * a.y;
			area += cross / 2;
			x += (a.x + b.x) * cross / 6;
			y += (a.y + b.y) * cross / 6;
		}
	}
	return area > 0 ? {x: x / area, y: y / area, r: Math.sqrt(area / Math.PI)} : {x: 0, y: 0, r: 0.5};
};

export const outlineOf = shapes => {
	const parts = shapes.map(shape => {
		if (shape.r !== undefined) return {shape, length: Math.PI * 2 * shape.r};
		const edges = shape.points.map((point, i) => {
			const next = shape.points[(i + 1) % shape.points.length];
			return Math.sqrt((next.x - point.x) * (next.x - point.x) + (next.y - point.y) * (next.y - point.y));
		});
		return {shape, edges, length: edges.reduce((sum, edge) => sum + edge, 0)};
	});
	return {parts, length: parts.reduce((sum, part) => sum + part.length, 0)};
};

// A point a fraction of the way round an outline, and the way out there.
export const outlinePoint = (outline, along) => {
	let distance = (along % 1) * outline.length;
	for (let p = 0; p < outline.parts.length; p++) {
		const {shape, edges, length} = outline.parts[p];
		if (distance > length && p < outline.parts.length - 1) {
			distance -= length;
			continue;
		}
		if (shape.r !== undefined) {
			const angle = distance / shape.r;
			const nx = Math.cos(angle);
			const ny = Math.sin(angle);
			return {x: shape.x + nx * shape.r, y: shape.y + ny * shape.r, nx, ny};
		}
		for (let i = 0; i < edges.length; i++) {
			if (distance > edges[i] && i < edges.length - 1) {
				distance -= edges[i];
				continue;
			}
			const a = shape.points[i];
			const b = shape.points[(i + 1) % shape.points.length];
			const t = Math.min(1, distance / (edges[i] || 1));
			return {x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, nx: shape.normals[i].x, ny: shape.normals[i].y};
		}
	}
	return {x: 0, y: 0, nx: 1, ny: 0};
};
